import React, { useState, useEffect } from 'react';
import './StylePromptsModal.css';
import { aiAPI } from '../utils/api';

// Dialogue style type
type DialogueStyleType = 'prose' | 'script';

interface StylePromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    storyStylePrompt?: string;
    visualStylePrompt?: string;
    storyType?: string;
    storyTone?: string;
    drawingMedium?: string;
    artStyle?: string;
    useReferenceBook?: boolean;
    referenceBook?: string;
    dialogueStyle?: DialogueStyleType;
  }) => void;
  currentStoryStylePrompt?: string;
  currentVisualStylePrompt?: string;
  currentStoryType?: string;
  currentStoryTone?: string;
  currentDrawingMedium?: string;
  currentArtStyle?: string;
  currentUseReferenceBook?: boolean;
  currentReferenceBook?: string;
  currentDialogueStyle?: DialogueStyleType;
}

const StylePromptsModal: React.FC<StylePromptsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentStoryStylePrompt = '',
  currentVisualStylePrompt = '',
  currentStoryType = 'Fairy Tale',
  currentStoryTone = 'Playful',
  currentDrawingMedium = 'Digital Art',
  currentArtStyle = 'Children\'s Book Illustration',
  currentUseReferenceBook = false,
  currentReferenceBook = '',
  currentDialogueStyle = 'prose'
}) => {
  // Dialogue style state
  const [dialogueStyle, setDialogueStyle] = useState<DialogueStyleType>(currentDialogueStyle);
  
  const [storyStylePrompt, setStoryStylePrompt] = useState(currentStoryStylePrompt);
  const [visualStylePrompt, setVisualStylePrompt] = useState(currentVisualStylePrompt);
  const [storyType, setStoryType] = useState(currentStoryType);
  const [storyTone, setStoryTone] = useState(currentStoryTone);
  const [drawingMedium, setDrawingMedium] = useState(currentDrawingMedium);
  const [artStyle, setArtStyle] = useState(currentArtStyle);
  const [useReferenceBook, setUseReferenceBook] = useState(currentUseReferenceBook);
  const [referenceBook, setReferenceBook] = useState(currentReferenceBook);
  const [customBookName, setCustomBookName] = useState('');
  const [generatingPrompts, setGeneratingPrompts] = useState(false);
  
  const [hasUserChangedDropdowns, setHasUserChangedDropdowns] = useState(false);

  console.log('=== STORY SETTINGS MODAL STATE ===');
  console.log('Props - storyStylePrompt:', currentStoryStylePrompt);
  console.log('Props - visualStylePrompt:', currentVisualStylePrompt);
  console.log('Props - useReferenceBook:', currentUseReferenceBook);
  console.log('Props - referenceBook:', currentReferenceBook);
  console.log('State - storyStylePrompt:', storyStylePrompt);
  console.log('State - visualStylePrompt:', visualStylePrompt);
  console.log('State - useReferenceBook:', useReferenceBook);
  console.log('State - referenceBook:', referenceBook);

  // Sync with props when they change (e.g., when modal opens)
  useEffect(() => {
    setStoryStylePrompt(currentStoryStylePrompt);
    setVisualStylePrompt(currentVisualStylePrompt);
    setStoryType(currentStoryType);
    setStoryTone(currentStoryTone);
    setDrawingMedium(currentDrawingMedium);
    setArtStyle(currentArtStyle);
    setUseReferenceBook(currentUseReferenceBook);
    setReferenceBook(currentReferenceBook);
    setDialogueStyle(currentDialogueStyle || 'prose');
    // Reset the flag when props sync (modal opened fresh)
    setHasUserChangedDropdowns(false);
  }, [currentStoryStylePrompt, currentVisualStylePrompt, currentStoryType, currentStoryTone, 
      currentDrawingMedium, currentArtStyle, currentUseReferenceBook, currentReferenceBook, 
      currentDialogueStyle]);

  // Generate default prompts based on selections
  const generateDefaultPrompts = () => {
    const storyPrompt = `Write in the style of a ${storyType.toLowerCase()} with a ${storyTone.toLowerCase()} tone. Use age-appropriate language that is engaging and easy to understand for young readers.`;
    
    const visualPrompt = `Illustrated in ${drawingMedium.toLowerCase()} style with ${artStyle.toLowerCase()} artistic approach. The illustrations should be colorful, engaging, and appropriate for children's books.`;
    
    setStoryStylePrompt(storyPrompt);
    setVisualStylePrompt(visualPrompt);
  };

  // Auto-update prompts when dropdowns change (only if user has made changes and not using reference book)
  useEffect(() => {
    // Only auto-generate if user has actively changed dropdowns (not on initial load)
    if (hasUserChangedDropdowns && !useReferenceBook) {
      generateDefaultPrompts();
    }
  }, [storyType, storyTone, drawingMedium, artStyle, useReferenceBook, hasUserChangedDropdowns]);

  const handleGenerateFromBookName = async () => {
    if (!customBookName.trim()) {
      alert('Please enter a book name');
      return;
    }

    setGeneratingPrompts(true);
    try {
      const response = await aiAPI.generateBookPrompts(customBookName.trim());
      
      if (response.success && response.data) {
        setStoryStylePrompt(response.data.storyStylePrompt);
        setVisualStylePrompt(response.data.visualStylePrompt);
        setReferenceBook(customBookName.trim());
        setUseReferenceBook(true);
        setCustomBookName('');
      }
    } catch (error: any) {
      console.error('Failed to generate prompts:', error);
      alert(error.response?.data?.error || 'Failed to generate prompts. Please try again.');
    } finally {
      setGeneratingPrompts(false);
    }
  };

  const handleSave = () => {
    onSave({
      storyStylePrompt,
      visualStylePrompt,
      storyType,
      storyTone,
      drawingMedium,
      artStyle,
      useReferenceBook,
      referenceBook: useReferenceBook ? referenceBook : undefined,
      dialogueStyle
    });
    onClose();
  };

  if (!isOpen) return null;

  // Match the options from StorybookWizard for consistency
  const storyTypes = [
    'Fairy Tale',
    'Adventure',
    'Mystery',
    'Fantasy',
    'Science Fiction',
    'Animal Story',
    'Educational',
    'Bedtime Story',
    'Realistic Fiction',
    'Custom'
  ];
  
  const storyTones = [
    'Playful',
    'Whimsical',
    'Adventurous',
    'Mysterious',
    'Heartwarming',
    'Educational',
    'Funny',
    'Calm',
    'Serious',
    'Humorous',
    'Inspiring'
  ];
  
  const drawingMediums = [
    'Digital Art',
    'Pencil',
    'Colored Pencil',
    'Pen',
    'Ink',
    'Marker',
    'Crayon',
    'Pastel',
    'Chalk',
    'Charcoal',
    'Watercolor',
    'Acrylic Paint',
    'Oil Paint',
    'Gouache',
    'Airbrush',
    'Spray Paint',
    'Mixed Media',
    'Cartoon',
    'Collage',
    'Custom'
  ];
  
  const artStyles = [
    'Children\'s Book Illustration',
    'Storybook Art',
    'Cartoon',
    'Anime/Manga',
    'Comic Book',
    'Studio Ghibli Style',
    'Disney Style',
    'Pixar Style',
    'Fantasy Art',
    'Realistic',
    'Semi-Realistic',
    'Stylized',
    'Sketch',
    'Line Art',
    'Pixel Art',
    'Vector Art',
    '3D Rendered',
    'Flat Design',
    'Minimalist',
    'Art Nouveau',
    'Art Deco',
    'Impressionist',
    'Abstract',
    'Pop Art',
    'Paper Cut',
    'Collage',
    'Vintage',
    'Retro',
    'Photorealistic',
    'Whimsical',
    'Detailed',
    'Custom'
  ];

  return (
    <div className="style-prompts-overlay">
      <div className="style-prompts-modal">
        <div className="style-prompts-header">
          <h2>📖 Story Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="style-prompts-content">
          {/* Reference Book Section */}
          <div className="style-section">
            <h3>📚 Reference Book</h3>
            <p className="section-description">
              Base your storybook on the style of an existing children's book
            </p>
            
            <div className="reference-book-input-group">
              <input
                type="text"
                value={customBookName}
                onChange={(e) => setCustomBookName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleGenerateFromBookName()}
                placeholder="Enter a children's book name (e.g., 'The Very Hungry Caterpillar')"
                className="book-name-input"
                disabled={generatingPrompts}
              />
              <button
                onClick={handleGenerateFromBookName}
                disabled={generatingPrompts || !customBookName.trim()}
                className="btn btn-primary generate-prompts-btn"
              >
                {generatingPrompts ? '⏳ Analyzing...' : '✨ Generate Style'}
              </button>
            </div>

            {useReferenceBook && referenceBook && (
              <div className="current-reference">
                <span className="reference-label">Current reference:</span>
                <strong>{referenceBook}</strong>
                <button
                  onClick={() => {
                    setUseReferenceBook(false);
                    setReferenceBook('');
                    generateDefaultPrompts();
                  }}
                  className="btn-link"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="style-divider">OR</div>

          {/* Manual Style Selection */}
          <div className="style-section">
            <h3>🎨 Manual Style Selection</h3>
            <p className="section-description">
              Customize the story and visual style manually
            </p>

            <div className="style-grid">
              <div className="form-group">
                <label>Story Type:</label>
                <select
                  value={storyType}
                  onChange={(e) => { setStoryType(e.target.value); setHasUserChangedDropdowns(true); }}
                  disabled={useReferenceBook}
                >
                  {storyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Story Tone:</label>
                <select
                  value={storyTone}
                  onChange={(e) => { setStoryTone(e.target.value); setHasUserChangedDropdowns(true); }}
                  disabled={useReferenceBook}
                >
                  {storyTones.map(tone => (
                    <option key={tone} value={tone}>{tone}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Drawing Medium:</label>
                <select
                  value={drawingMedium}
                  onChange={(e) => { setDrawingMedium(e.target.value); setHasUserChangedDropdowns(true); }}
                  disabled={useReferenceBook}
                >
                  {drawingMediums.map(medium => (
                    <option key={medium} value={medium}>{medium}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Art Style:</label>
                <select
                  value={artStyle}
                  onChange={(e) => { setArtStyle(e.target.value); setHasUserChangedDropdowns(true); }}
                  disabled={useReferenceBook}
                >
                  {artStyles.map(style => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Dialogue Style Selection */}
            <div className="dialogue-style-section">
              <h4>💬 Dialogue Style</h4>
              <p className="section-description">
                Choose how character dialogue is presented in your story
              </p>
              <div className="dialogue-style-options">
                <label 
                  className={`dialogue-style-card ${dialogueStyle === 'prose' ? 'selected' : ''}`}
                  onClick={() => !useReferenceBook && setDialogueStyle('prose')}
                >
                  <input
                    type="radio"
                    name="dialogueStyle"
                    value="prose"
                    checked={dialogueStyle === 'prose'}
                    onChange={() => setDialogueStyle('prose')}
                    disabled={useReferenceBook}
                  />
                  <div className="dialogue-style-content">
                    <span className="dialogue-style-name">📖 Narrative Prose</span>
                    <span className="dialogue-style-example">
                      Kiki shouts "A kitty superhero is ready!"
                    </span>
                  </div>
                </label>
                <label 
                  className={`dialogue-style-card ${dialogueStyle === 'script' ? 'selected' : ''}`}
                  onClick={() => !useReferenceBook && setDialogueStyle('script')}
                >
                  <input
                    type="radio"
                    name="dialogueStyle"
                    value="script"
                    checked={dialogueStyle === 'script'}
                    onChange={() => setDialogueStyle('script')}
                    disabled={useReferenceBook}
                  />
                  <div className="dialogue-style-content">
                    <span className="dialogue-style-name">💭 Comic Script</span>
                    <span className="dialogue-style-example">
                      Kiki: A kitty superhero is ready!
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Style Prompts */}
          <div className="style-section">
            <h3>📝 Story Style Prompt</h3>
            <p className="section-description">
              This guides how the story text is written
            </p>
            <textarea
              value={storyStylePrompt}
              onChange={(e) => setStoryStylePrompt(e.target.value)}
              rows={4}
              className="prompt-textarea"
              placeholder="Describe the narrative style, tone, language complexity, and storytelling approach..."
            />
          </div>

          <div className="style-section">
            <h3>🎨 Visual Style Prompt</h3>
            <p className="section-description">
              This guides how images are generated
            </p>
            <textarea
              value={visualStylePrompt}
              onChange={(e) => setVisualStylePrompt(e.target.value)}
              rows={4}
              className="prompt-textarea"
              placeholder="Describe the illustration style, artistic medium, colors, and visual characteristics..."
            />
          </div>
        </div>

        <div className="style-prompts-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn btn-primary">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default StylePromptsModal;
